import { Option, Schema, Struct } from "effect";
import { schemaParser } from "./schema-compat.ts";
import { IdentityArrayInput, IdentityObjectInput } from "./identity-input.ts";

import {
  redactOpaqueUrlsInText,
  redactedOpaqueUrlProtocol
} from "./url-redaction.ts";
import { AgenticTraceContextInput } from "./agentic-correlation.ts";

export const BrowserObservationDirection = /* @__PURE__ */ (() => Schema.Literals(["client-to-browser", "browser-to-client", "binding"]))();
export type BrowserObservationDirection = typeof BrowserObservationDirection.Type;
export const BrowserObservationKind = /* @__PURE__ */ (() => Schema.Literals(["cdp-command", "cdp-response", "cdp-event", "transport", "session", "quick-action"]))();
export type BrowserObservationKind = typeof BrowserObservationKind.Type;
export const BrowserObservationLevel = /* @__PURE__ */ (() => Schema.Literals(["debug", "info", "warning", "error"]))();
export type BrowserObservationLevel = typeof BrowserObservationLevel.Type;

export const BrowserObservationContext = /* @__PURE__ */ (() => Schema.Struct({
  ...AgenticTraceContextInput.fields,
  workspaceId: Schema.String.pipe(Schema.mutableKey),
  stationId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  workerName: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  bindingName: Schema.String.pipe(Schema.mutableKey),
  namespace: Schema.String.pipe(Schema.mutableKey),
}))();
export type BrowserObservationContext = typeof BrowserObservationContext.Type;

export const BrowserObservationInput = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  timestamp: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  sessionId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  protocolSessionId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  direction: BrowserObservationDirection.pipe(Schema.mutableKey),
  kind: BrowserObservationKind.pipe(Schema.mutableKey),
  category: Schema.String.pipe(Schema.mutableKey),
  level: BrowserObservationLevel.pipe(Schema.mutableKey),
  method: Schema.String.pipe(Schema.mutableKey),
  summary: Schema.String.pipe(Schema.mutableKey),
  url: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  requestId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  frameId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  payload: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}))();
export type BrowserObservationInput = typeof BrowserObservationInput.Type;

export const BrowserObservationEvent = /* @__PURE__ */ (() => Schema.Struct({
  ...BrowserObservationContext.fields,
  ...Schema.Struct(Struct.omit(BrowserObservationInput.fields, ["id", "timestamp"])).fields,
  id: Schema.String.pipe(Schema.mutableKey),
  sequence: Schema.Number.pipe(Schema.mutableKey),
  timestamp: Schema.Number.pipe(Schema.mutableKey),
  sizeBytes: Schema.Number.pipe(Schema.mutableKey),
}))();
export type BrowserObservationEvent = typeof BrowserObservationEvent.Type;

export const BrowserObservationSession = /* @__PURE__ */ (() => Schema.Struct({
  sessionId: Schema.String.pipe(Schema.mutableKey),
  bindingName: Schema.String.pipe(Schema.mutableKey),
  workerName: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  firstSeen: Schema.Number.pipe(Schema.mutableKey),
  lastSeen: Schema.Number.pipe(Schema.mutableKey),
  eventCount: Schema.Number.pipe(Schema.mutableKey),
  errorCount: Schema.Number.pipe(Schema.mutableKey),
}))();
export type BrowserObservationSession = typeof BrowserObservationSession.Type;

export const BrowserObservationStats = /* @__PURE__ */ (() => Schema.Struct({
  eventCount: Schema.Number.pipe(Schema.mutableKey),
  sessionCount: Schema.Number.pipe(Schema.mutableKey),
  errorCount: Schema.Number.pipe(Schema.mutableKey),
  warningCount: Schema.Number.pipe(Schema.mutableKey),
  commandCount: Schema.Number.pipe(Schema.mutableKey),
  eventBytes: Schema.Number.pipe(Schema.mutableKey),
  categories: Schema.Array(Schema.Struct({
    category: Schema.String.pipe(Schema.mutableKey),
    count: Schema.Number.pipe(Schema.mutableKey),
  })).pipe(Schema.mutable, Schema.mutableKey),
  levels: Schema.Array(Schema.Struct({
    level: BrowserObservationLevel.pipe(Schema.mutableKey),
    count: Schema.Number.pipe(Schema.mutableKey),
  })).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type BrowserObservationStats = typeof BrowserObservationStats.Type;

export const BrowserObservationSnapshot = /* @__PURE__ */ (() => Schema.Struct({
  events: Schema.Array(BrowserObservationEvent).pipe(Schema.mutable, Schema.mutableKey),
  sessions: Schema.Array(BrowserObservationSession).pipe(Schema.mutable, Schema.mutableKey),
  stats: BrowserObservationStats.pipe(Schema.mutableKey),
  generatedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type BrowserObservationSnapshot = typeof BrowserObservationSnapshot.Type;

export const BrowserObservationQuery = /* @__PURE__ */ (() => Schema.Struct({
  ...AgenticTraceContextInput.fields,
  stationId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  sessionId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  bindingName: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  workerName: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  direction: BrowserObservationDirection.pipe(Schema.optional, Schema.mutableKey),
  kind: BrowserObservationKind.pipe(Schema.optional, Schema.mutableKey),
  category: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  level: BrowserObservationLevel.pipe(Schema.optional, Schema.mutableKey),
  method: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  search: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  since: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  before: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  limit: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
}))();
export type BrowserObservationQuery = typeof BrowserObservationQuery.Type;

export const BrowserCommandIdentity = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.Union([Schema.String, Schema.Number]),
  method: Schema.String
}))();
export type BrowserCommandIdentity = typeof BrowserCommandIdentity.Type;
export const BrowserResponseId = /* @__PURE__ */ (() => Schema.Union([Schema.String, Schema.Number]))();
export type BrowserResponseId = typeof BrowserResponseId.Type;

const isString = /* @__PURE__ */ (() => Schema.is(Schema.String))();
const isNonEmptyString = /* @__PURE__ */ (() => Schema.is(Schema.String.check(Schema.isMinLength(1))))();
const isNullish = /* @__PURE__ */ (() => Schema.is(Schema.Union([Schema.Null, Schema.Undefined])))();
const isNumericOrBoolean = /* @__PURE__ */ (() => Schema.is(Schema.Union([Schema.Number, Schema.Boolean])))();
const isBigInt = /* @__PURE__ */ (() => Schema.is(Schema.BigInt))();
const isSymbol = /* @__PURE__ */ (() => Schema.is(Schema.Symbol))();
const isCallable = /* @__PURE__ */ (() => Schema.is(Schema.declare((value): value is Function => typeof value === "function")))();
const isObject = /* @__PURE__ */ (() => Schema.is(IdentityObjectInput))();
const objectInput = /* @__PURE__ */ (() => schemaParser(IdentityObjectInput))();
const isArray = /* @__PURE__ */ (() => Schema.is(IdentityArrayInput))();
const isDate = /* @__PURE__ */ (() => Schema.is(Schema.instanceOf(Date)))();
const isUrl = /* @__PURE__ */ (() => Schema.is(Schema.instanceOf(URL)))();
const isError = /* @__PURE__ */ (() => Schema.is(Schema.instanceOf(Error)))();
const BrowserBinaryInput = /* @__PURE__ */ (() => Schema.Union([
  Schema.instanceOf(ArrayBuffer),
  Schema.declare((value): value is ArrayBufferView => ArrayBuffer.isView(value))
]))();
const isBinary = /* @__PURE__ */ (() => Schema.is(BrowserBinaryInput))();
const optionalBinary = /* @__PURE__ */ (() => Schema.decodeUnknownOption(BrowserBinaryInput))();
const optionalString = (value: unknown) => Option.getOrUndefined(Schema.decodeUnknownOption(Schema.String)(value));
const optionalArray = (value: unknown) => Option.getOrUndefined(Schema.decodeUnknownOption(IdentityArrayInput)(value));
const optionalObject = (value: unknown) => Option.getOrUndefined(Schema.decodeUnknownOption(IdentityObjectInput)(value));
const messageInput = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.fromJsonString(Schema.Record(Schema.String, Schema.Unknown))))();
const commandIdentityInput = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.fromJsonString(BrowserCommandIdentity)))();
const responseIdentityInput = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.fromJsonString(Schema.Struct({ id: BrowserResponseId }))))();

const SENSITIVE_KEY =
  /(?:authorization|proxy-authorization|cookie|set-cookie|secret|token|password|credential|private[-_]?key|api[-_]?key|client[-_]?secret|postData|responseBody|requestBody|payloadData|webSocketFrame|html)/i;
const SECRET_VALUE =
  /(?:\bBearer\s+\S+|\b(?:token|secret|password|credential|api[-_]?key|client[-_]?secret|access[-_]?key|private[-_]?key)\s*[:=]\s*\S+|\beyJ[a-zA-Z0-9_-]{20,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\b)/i;
const HIGH_ENTROPY_VALUE = /\b(?:[a-f0-9]{64,}|[a-zA-Z0-9_+/=-]{96,})\b/;
const URL_KEY = /(?:^|_)(?:url|uri)$/i;
const CAMEL_URL_KEY = /(?:URL|URI|Url|Uri)$/;
const URL_CONTEXT_KEY = /referer|referrer|location/i;
const RETENTION_MS = 7 * 24 * 60 * 60 * 1_000;
const EVENT_LIMIT = 20_000;
const PAYLOAD_CHARACTER_LIMIT = 24_000;
const SUMMARY_LIMIT = 1_000;
const isSensitiveKey = /* @__PURE__ */ (() => Schema.is(Schema.String.check(Schema.isPattern(SENSITIVE_KEY))))();
const isSecretValue = /* @__PURE__ */ (() => Schema.is(Schema.String.check(Schema.makeFilter(value =>
  SECRET_VALUE.test(value) || HIGH_ENTROPY_VALUE.test(value)
))))();

export const BROWSER_OBSERVABILITY_POLICY = {
  capture: "all-cdp-messages",
  retentionMs: RETENTION_MS,
  eventLimit: EVENT_LIMIT,
  payloadCharacterLimit: PAYLOAD_CHARACTER_LIMIT,
  sanitized: true
} as const;

export function sanitizeBrowserUrl(value: string): string {
  try {
    const url = new URL(value);
    const opaque = redactedOpaqueUrlProtocol(url.protocol);
    if (opaque) return opaque;
    url.username = "";
    url.password = "";
    for (const name of [...url.searchParams.keys()]) {
      url.searchParams.set(name, "[REDACTED]");
    }
    url.hash = "";
    return url.toString().slice(0, 2_000);
  } catch {
    return sanitizeBrowserText(value, 2_000);
  }
}

function isBrowserUrlKey(key: string): boolean {
  return (
    URL_KEY.test(key) || CAMEL_URL_KEY.test(key) || URL_CONTEXT_KEY.test(key)
  );
}

export function sanitizeBrowserText(value: string, limit = SUMMARY_LIMIT): string {
  if (SECRET_VALUE.test(value) || HIGH_ENTROPY_VALUE.test(value)) {
    return "[REDACTED]";
  }
  return redactOpaqueUrlsInText(value)
    .replace(/([?&][^=\s&]{1,80}=)[^&\s]+/g, "$1[REDACTED]")
    .slice(0, Math.max(1, limit));
}

function redactedValue(value: string): Record<string, unknown> {
  return {
    type: "string",
    length: value.length,
    value: "[REDACTED]"
  };
}

/**
 * Preserve high-detail CDP diagnostics while bounding traversal and removing
 * credentials, request bodies, WebSocket frame payloads, and URL values.
 */
export function sanitizeBrowserPayload(
  value: unknown,
  key?: string,
  depth = 0,
  seen = new WeakSet<object>(),
  budget = { remaining: 1_000 }
): unknown {
  budget.remaining -= 1;
  if (budget.remaining < 0) {
    return { type: "budget-exhausted", truncated: true };
  }
  if (isNullish(value)) return null;
  if (isString(value)) {
    if (key && isSensitiveKey(key)) return redactedValue(value);
    if (key && isBrowserUrlKey(key)) return sanitizeBrowserUrl(value);
    if (isSecretValue(value)) {
      return redactedValue(value);
    }
    const safe = redactOpaqueUrlsInText(value);
    return safe.length > 2_000 ? `${safe.slice(0, 2_000)}…` : safe;
  }
  if (isNumericOrBoolean(value)) return value;
  if (isBigInt(value)) return String(value);
  if (isCallable(value)) {
    return { type: "function", name: value.name || "anonymous" };
  }
  if (isSymbol(value)) return { type: "symbol" };
  const object = objectInput(value);
  if (isBinary(object)) {
    return {
      type: object.constructor.name,
      bytes: object.byteLength
    };
  }
  if (isDate(object)) return object.toISOString();
  if (isUrl(object)) return sanitizeBrowserUrl(object.toString());
  if (isError(object)) {
    return {
      type: object.constructor.name,
      message: sanitizeBrowserText(object.message)
    };
  }
  if (seen.has(object)) return { type: "circular" };
  if (depth >= 8) {
    return { type: object.constructor?.name || "object", truncated: true };
  }
  seen.add(object);

  if (isArray(object)) {
    const values = object
      .slice(0, 50)
      .map((entry) =>
        sanitizeBrowserPayload(entry, undefined, depth + 1, seen, budget)
      );
    return object.length > 50
      ? { type: "array", length: object.length, values, truncated: true }
      : values;
  }

  const entries = Object.entries(object);
  const output: Record<string, unknown> = {};
  for (const [name, entry] of entries.slice(0, 100)) {
    output[name] = sanitizeBrowserPayload(
      entry,
      name,
      depth + 1,
      seen,
      budget
    );
  }
  if (entries.length > 100) output.truncatedKeys = entries.length - 100;
  return output;
}

function remoteObjectText(value: unknown): string | undefined {
  if (!isObject(value)) return undefined;
  const candidate =
    Reflect.get(value, "value") ?? Reflect.get(value, "description") ?? Reflect.get(value, "unserializableValue");
  if (candidate === undefined) return Reflect.get(value, "type") ? String(Reflect.get(value, "type")) : undefined;
  if (isString(candidate)) return sanitizeBrowserText(candidate, 500);
  try {
    return sanitizeBrowserText(JSON.stringify(candidate), 500);
  } catch {
    return sanitizeBrowserText(String(candidate), 500);
  }
}

function nestedRecord(
  value: Record<string, unknown>,
  ...path: string[]
): object | undefined {
  let current: unknown = value;
  for (const segment of path) {
    const object = optionalObject(current);
    if (!object) return undefined;
    current = Reflect.get(object, segment);
  }
  return optionalObject(current);
}

function nestedString(
  value: Record<string, unknown>,
  ...paths: string[][]
): string | undefined {
  for (const path of paths) {
    let current: unknown = value;
    for (const segment of path) {
      const object = optionalObject(current);
      if (!object) {
        current = undefined;
        break;
      }
      current = Reflect.get(object, segment);
    }
    if (isNonEmptyString(current)) return current;
  }
  return undefined;
}

function browserLevel(
  method: string,
  message: Record<string, unknown>
): BrowserObservationLevel {
  const explicit = nestedString(
    message,
    ["params", "type"],
    ["params", "entry", "level"],
    ["params", "level"]
  )?.toLowerCase();
  if (
    explicit === "error" ||
    explicit === "assert" ||
    method === "Runtime.exceptionThrown" ||
    method === "Network.loadingFailed" ||
    method.endsWith(".targetCrashed") ||
    method === "Inspector.detached" ||
    method === "Security.certificateError" ||
    method === "Audits.issueAdded" ||
    message.error !== undefined
  ) {
    return "error";
  }
  if (
    explicit === "warning" ||
    explicit === "warn" ||
    method.includes("warning") ||
    method.includes("Blocked")
  ) {
    return "warning";
  }
  if (
    explicit === "debug" ||
    explicit === "verbose" ||
    method.startsWith("Network.data") ||
    method.endsWith("EventFired") ||
    method.endsWith(".metrics")
  ) {
    return "debug";
  }
  return "info";
}

function browserSummary(
  method: string,
  message: Record<string, unknown>,
  kind: BrowserObservationKind
): string {
  if (method === "Runtime.consoleAPICalled") {
    const params = nestedRecord(message, "params");
    const args = optionalArray(params && Reflect.get(params, "args")) ?? [];
    const text = args
      .slice(0, 10)
      .map(remoteObjectText)
      .filter((entry): entry is string => Boolean(entry))
      .join(" ");
    const type = optionalString(params && Reflect.get(params, "type")) ?? "console";
    return sanitizeBrowserText(`${type}: ${text || "console call"}`);
  }
  if (method === "Runtime.exceptionThrown") {
    const text = nestedString(
      message,
      ["params", "exceptionDetails", "exception", "description"],
      ["params", "exceptionDetails", "text"]
    );
    return sanitizeBrowserText(text ? `exception: ${text}` : method);
  }
  if (method === "Log.entryAdded") {
    const level = nestedString(message, ["params", "entry", "level"]);
    const text = nestedString(message, ["params", "entry", "text"]);
    return sanitizeBrowserText(
      `${level ?? "log"}: ${text ?? "browser log entry"}`
    );
  }
  if (method === "Network.loadingFailed") {
    const text = nestedString(message, ["params", "errorText"]);
    return sanitizeBrowserText(text ? `${method}: ${text}` : method);
  }
  const url = nestedString(
    message,
    ["params", "request", "url"],
    ["params", "response", "url"],
    ["params", "frame", "url"],
    ["params", "documentURL"]
  );
  if (url) return `${method} ${sanitizeBrowserUrl(url)}`.slice(0, SUMMARY_LIMIT);
  const id = message.id;
  if (kind === "cdp-response" && id !== undefined) {
    return `${method || "CDP response"} #${String(id)}${message.error ? " error" : " ok"}`;
  }
  return method || kind;
}

const binaryBytes = (data: unknown): number | undefined =>
  Option.getOrUndefined(optionalBinary(data))?.byteLength;

/** Convert one raw DevTools transport frame into a persisted observation. */
export function browserObservationFromMessage(
  direction: Exclude<BrowserObservationDirection, "binding">,
  data: unknown,
  sessionId?: string,
  commandMethod?: string
): BrowserObservationInput {
  const bytes = binaryBytes(data);
  if (bytes !== undefined) {
    return {
      sessionId,
      direction,
      kind: "transport",
      category: "transport",
      level: "debug",
      method: "Browser.transport.binary",
      summary: `Binary DevTools frame (${bytes} bytes)`,
      payload: { type: "binary", bytes }
    };
  }

  const text = isString(data) ? data : String(data);
  // Failed JSON/object decoding retains bounded transport metadata.
  const message = Option.getOrUndefined(messageInput(text));
  if (!message) {
    return {
      sessionId,
      direction,
      kind: "transport",
      category: "transport",
      level: "debug",
      method: "Browser.transport.text",
      summary: `Non-JSON DevTools frame (${text.length} characters)`,
      payload: { length: text.length, preview: sanitizeBrowserText(text, 1_000) }
    };
  }

  const rawMethod = optionalString(message.method) ?? "";
  const method = rawMethod || commandMethod || "Browser.protocol.message";
  const kind: BrowserObservationKind = rawMethod
    ? direction === "client-to-browser"
      ? "cdp-command"
      : "cdp-event"
    : message.id !== undefined
      ? "cdp-response"
      : "transport";
  const category = method.includes(".")
    ? method.slice(0, method.indexOf(".")).toLowerCase()
    : "protocol";
  const url = nestedString(
    message,
    ["params", "request", "url"],
    ["params", "response", "url"],
    ["params", "frame", "url"],
    ["params", "documentURL"]
  );
  return {
    sessionId,
    protocolSessionId: optionalString(message.sessionId),
    direction,
    kind,
    category,
    level: browserLevel(method, message),
    method,
    summary: browserSummary(method, message, kind),
    ...(url ? { url: sanitizeBrowserUrl(url) } : {}),
    ...(nestedString(message, ["params", "requestId"])
      ? { requestId: nestedString(message, ["params", "requestId"]) }
      : {}),
    ...(nestedString(
      message,
      ["params", "frameId"],
      ["params", "frame", "id"]
    )
      ? {
          frameId: nestedString(
            message,
            ["params", "frameId"],
            ["params", "frame", "id"]
          )
        }
      : {}),
    payload: sanitizeBrowserPayload(message)
  };
}

export function browserObservationCommandIdentity(
  data: unknown
): BrowserCommandIdentity | undefined {
  return Option.getOrUndefined(commandIdentityInput(data));
}

export function browserObservationResponseId(
  data: unknown
): BrowserResponseId | undefined {
  return Option.getOrUndefined(responseIdentityInput(data))?.id;
}
